import Moment from 'moment';
import {differenceInCalendarDays} from 'date-fns';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {createRefetchContainer, graphql, RelayRefetchProp} from 'react-relay';
import styled from 'styled-components';
import * as OsUtil from '../../../../../util/OsUtil';

import {
	Alert,
	FlexColumn,
	FlexRow,
	Label,
	Text,
	TextArea,
	DeprecatedTimeRegCreativeModal as TimeRegCreativeModal,
} from '@forecast-it/design-system';
import {AlertType} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/alert/types';

import {asFragment} from '../../../../../../../relay-helpers/TSRelayConverter';
import {TRACKING_OBJECTS} from '../../../../../../../tracking/amplitude/constants/TrackingObjects';
import {useTrackModal} from '../../../../../../../tracking/amplitude/hooks/useTrackModal';
import {trackEvent} from '../../../../../../../tracking/amplitude/TrackingV2';
import {workingHourForTheDay} from '../../../../../../my-work-tab/my-timesheets-page/timesheets_person_data';
import {getAllowedEntitiesText} from '../../../../../../navigation/header/global-search/formatters/FormatterUtil';
import {SEARCH_ENTITIES} from '../../../../../../navigation/header/global-search/GlobalSearchModal';
import {usePrevious} from '../../../../../hooks/usePreviousPersistent';
import useUpdateEffect from '../../../../../hooks/useUpdateEffect';
import {useWindowSize} from '../../../../../hooks/useWindowSize';
import {
	isBillableSplitAllowed,
	isInternalTimeRegistrationAllowed,
	isProjectTimeRegistrationAllowed,
	isRolesAllowed,
	isTimeRegistrationNoteRequired,
} from '../../../../../util/cache/TimeRegistrationSettingsUtil';
import {getBillableMinutesForMutation} from '../../../../../util/time-registration/time-registration-settings/BillableTimeSplitUtil';
import {isTimeRegistrationTimeInvalidWithReason} from '../../../../../util/time-registration/time-registration-settings/TimeRegistrationTimeFilter';
import Util from '../../../../../util/util';
import TimerTaskInvalidInfo from '../../../../add-time-entry/TimerTaskInvalidInfo';
import {TimeRegGranularityInfoIcon} from '../../../../add-time-entry/validation-info/TimeRegGranularityInfo';
import {TimeRegistrationSettingsInfoMessage} from '../../../../add-time-entry/validation-info/TimeRegistrationSettingsInfoMessage';
import SkylineRoleDropdownRelayWrapper from '../../../../dropdowns/role-dropdown/skyline/SkylineRoleDropdownRelayWrapper';
import {convertIntoFloatHoursFormatWithGranularity} from '../../../../inputs/hours-input/hours_input_logic';
import {HoursInput} from '../../../../inputs/hours-input/hours_input_view';
import TimeRegWSuggestionsInput from '../../../../inputs/time-registration-input/time_reg_w_sugestions_input';
import {MODAL_TYPE, showModal} from '../../../../modals/generic_modal_conductor';
import DeprecatedScrollToNativeScroll from '../../../../scroll-bars/DeprecatedScrollToNativeScroll';
import {isModalInputInvalid, ModalInvalidInputReason, validateTargetEntity} from '../logic/TimeRegistrationModalValidation';
import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';
import {Role} from '../types/TimeRegistrationRole';
import {
	getCompanyOnlyLockDateFromViewer,
	getInitialOrFirstNonLockValidDateForTargetEntity,
	getIsDateDisabledFromTargetEntity,
	getLockDateFromViewer,
} from '../util/TimeRegistrationModalDateUtil';
import {isDateHeldBySubmissionLock, useIsPersonWeekDeclined} from '../logic/useIsPersonWeekDeclined';
import {getDefaultRole, updateRoleOnProjectChange} from '../util/TimeRegistrationModalRoleUtil';
import {
	doesTargetEntityAllowRoles,
	getEntityProject,
	isProjectEntity,
	isTargetEntityBillable,
	isTaskEntity,
	searchResultToBackendEntityId,
} from '../util/TimeRegistrationModalUtil';
import TimeRegistrationModalDates from './TimeRegistrationModalDates';
import type {FormattedHarvestTask, FormattedUnit4Activity} from './TimeRegistrationModalIntegrations';
import TimeRegistrationModalIntegrations from './TimeRegistrationModalIntegrations';
import TimeRegistrationModalSearch, {FormattedTargetEntity} from './TimeRegistrationModalSearch';
import {TimeRegistrationModalRetainerPeriod} from './TimeRegistrationModalRetainerPeriod';
import {panelEntryMinutes} from '../logic/retainerPeriodPanelModel';
import TimeRegistrationModalTaskInfo from './TimeRegistrationModalTaskInfo';

import {EVENT_ID} from '../../../../../../../containers/event_manager';
import useSubscription from '../../../../../hooks/useSubscription';
import {idFromGlobalId} from '../../../../../util/GlobalIdUtil';
import {useSpinner} from '../../../../spinner/useSpinner';
import type {TimeRegistrationMutationInput} from '../logic/TimeRegistrationModalMutation';
import {TimeRegistrationInitialState} from '../types/TimeRegistrationInitialState';
import type {TimeRegistrationModal_entity, TimeRegistrationModal_task} from '../types/TimeRegistrationModalEntity';
import type {TimeRegistrationModalInfo} from '../types/TimeRegistrationModalInfo';
import type {TimeRegistrationModal_viewer$data as TimeRegistrationModal_viewer} from './__generated__/TimeRegistrationModal_viewer.graphql';
import type {TimeRegistrationModalIntegrations_company$data as TimeRegistrationModalIntegrations_company} from './__generated__/TimeRegistrationModalIntegrations_company.graphql';
import type {TimeRegistrationModalIntegrations_person$data as TimeRegistrationModalIntegrations_person} from './__generated__/TimeRegistrationModalIntegrations_person.graphql';
import type {TimeRegistrationModalIntegrations_project$data as TimeRegistrationModalIntegrations_project} from './__generated__/TimeRegistrationModalIntegrations_project.graphql';
import type {TimeRegistrationModalTaskInfo_task$data as TimeRegistrationModalTaskInfo_task} from './__generated__/TimeRegistrationModalTaskInfo_task.graphql';

function getPrimaryTargetEntityFromViewer(viewer: TimeRegistrationModal_viewer): TimeRegistrationModal_entity | null {
	// The primary target entity is only a task if no project is also selected/loaded
	if (viewer.task && !viewer.project) return {entity: TimeRegistrationEntity.Task, ...viewer.task};
	if (viewer.project) return {entity: TimeRegistrationEntity.Project, ...viewer.project};
	if (viewer.idleTime) return {entity: TimeRegistrationEntity.IdleTime, ...viewer.idleTime};
	return null;
}

function getSecondaryTargetEntityFromViewer(viewer: TimeRegistrationModal_viewer): TimeRegistrationModal_entity | null {
	// The secondary target entity is only a task if there is also a project selected/loaded
	if (viewer.project && viewer.task) return {entity: TimeRegistrationEntity.Task, ...viewer.task};
	return null;
}

const TextAreaWrapper = styled.div`
	margin-bottom: ${props => props.theme.sys.spacing[400]};
`;

const TaskSelectionWrapper = styled.div`
	display: flex;
	flex-direction: column;
	row-gap: ${props => props.theme.sys.spacing[100]};
	margin-top: ${props => props.theme.sys.spacing[300]};
	margin-bottom: ${props => props.theme.sys.spacing[100]};
`;

type CallAttentionProps = {
	callAttention: boolean;
};
const CallAttentionWrapper = styled.div<CallAttentionProps>`
	textarea,
	[role='spinbutton'] {
		animation: ${props => (props.callAttention ? 'callAttention 0.5s ease-in-out' : '')};
		animation-delay: 250ms;
		@keyframes callAttention {
			0% {
				background-color: ${({theme}) => theme.comp.calendar.color.background.calendarevent.hover};
			}
			100% {
				background-color: white;
			}
		}
	}
`;

type TimeRegistrationDefaultInitialState = {
	initialMinutes: number;
	initialDate: Date;
	initialNote: string;
	initialCallAttention: boolean;
	initialHarvestTaskId: FormattedHarvestTask | null;
	initialBillableTime: number | null;
};

export type TimeRegistrationModalBaseProps = {
	infoText: TimeRegistrationModalInfo;
	initialState?: TimeRegistrationInitialState;
	addInitialTimeToRemaining?: boolean;
	jiraTaskId?: string;
	/**
	 * Toggle whether to only validate individual form elements if they have been changed in the current session.
	 * This can for instance be used to allow the user to update some parts of a time registration where other parts are no longer allowed by the Time Registration settings.
	 */
	onlyValidateFormElementsOnChange?: boolean;
	shouldValidateEntity?: boolean;
	showValidationError?: boolean;
	onlyFormatGranularityOnBlur?: boolean;
	callbackPositive: (mutationInput: TimeRegistrationMutationInput) => void;
	resetTimer?: () => void;
	closeModal?: () => void;
	showAsModal?: boolean;
	fromTaskModal?: boolean;
	/** The most the form may grow before it scrolls; left out, it follows the window. */
	maxContentHeight?: number;
	/** The entry being edited, left out of its retainer period's registered hours. */
	timeRegistrationId?: string;
};

interface TimeRegistrationModalProps extends TimeRegistrationModalBaseProps {
	viewer: TimeRegistrationModal_viewer;
	relay: RelayRefetchProp;
	showCloseButton?: boolean;
	selectedPersonId: string | null;
}

function getTargetEntity(
	targetEntityPrimary: TimeRegistrationModal_entity | null,
	targetEntitySecondary: TimeRegistrationModal_entity | null
): TimeRegistrationModal_entity | null {
	return targetEntitySecondary ? targetEntitySecondary : targetEntityPrimary;
}

function getBillableTimeInputValue_timeRegInputConversion(
	targetEntityBillable: boolean,
	billableTime: number | null,
	time: number
): number | string {
	if (!targetEntityBillable) {
		return 0;
	}
	if (billableTime != null) {
		return `${billableTime}m`;
	}
	return `${time}m`;
}

const getDefaultInitialState = (initialState?: TimeRegistrationInitialState): TimeRegistrationDefaultInitialState => {
	return {
		initialMinutes: initialState?.minutes ?? 0,
		initialDate: initialState?.date ?? new Date(),
		initialNote: initialState?.note ?? '',
		initialCallAttention: initialState?.callAttention ?? false,
		initialHarvestTaskId: initialState?.harvestTaskId ? {value: initialState?.harvestTaskId} : null,
		initialBillableTime: initialState?.billableTime ?? null,
	};
};

function TimeRegistrationModal({
	viewer,
	relay,
	infoText,
	initialState,
	addInitialTimeToRemaining = false,
	jiraTaskId,
	onlyValidateFormElementsOnChange = false,
	shouldValidateEntity = false,
	showValidationError = false,
	callbackPositive,
	resetTimer,
	closeModal,
	showAsModal = true,
	fromTaskModal = false,
	maxContentHeight,
	timeRegistrationId,
	showCloseButton,
	selectedPersonId,
}: TimeRegistrationModalProps): JSX.Element {
	const hasSecondaryButton = !!infoText.secondaryButtonText && showAsModal;
	// The person's submission date folded in with any company lock. Used to choose the initial
	// date, before there is a selected date to know which week we are asking about.
	const submissionLockDate = getLockDateFromViewer(viewer);

	const {initialMinutes, initialDate, initialNote, initialCallAttention, initialHarvestTaskId, initialBillableTime} =
		getDefaultInitialState(initialState);

	const currentPersonId = selectedPersonId || viewer.actualPersonId;

	// The target entities are being fetch using refetch, so we don't have any states for the target entities other than the viewer object. So we have these helper functions to extract and validate the entities
	// from the viewer objects. Information about whether or not an entity belongs to the primary or secondary target entity, is inferred from the type of the entity.

	const {targetEntity: targetEntityPrimary, targetEntityError: targetEntityPrimaryError} = validateTargetEntity({
		targetEntity: getPrimaryTargetEntityFromViewer(viewer),
		personId: currentPersonId,
		shouldValidateEntity,
		showError: showValidationError,
	});

	const {targetEntity: targetEntitySecondary, targetEntityError: targetEntitySecondaryError} = validateTargetEntity({
		targetEntity: getSecondaryTargetEntityFromViewer(viewer),
		personId: currentPersonId,
		shouldValidateEntity,
		showError: showValidationError,
	});

	const [callAttention, setCallAttention] = useState(initialCallAttention);

	const [targetEntityFetching, setTargetEntityFetching] = useState(false);
	const [secondaryTargetEntityFetching, setSecondaryTargetEntityFetching] = useState(false);

	const targetEntity = getTargetEntity(targetEntityPrimary, targetEntitySecondary);

	const [time, setTime] = useState(initialMinutes);
	// The hours as typed, before the input commits them on blur, so the retainer period panel follows the typing and
	// does not grow under the pointer when Save is clicked.
	const [typedMinutes, setTypedMinutes] = useState<number | null>(null);
	// Saving an edit that moves the entry into a locked retainer period is refused, so Save is disabled instead.
	const [retainerMoveRefused, setRetainerMoveRefused] = useState(false);
	const [note, setNote] = useState(initialNote);
	const [timeError, setTimeError] = useState(false);
	const [billableTime, setBillableTime] = useState(initialBillableTime);
	// Seeded WITHOUT the submission lock on purpose. The week question below is asked about this
	// date, and advancing past the lock first would ask about the week AFTER the intended one
	// whenever the lock ends on the week's last day — which is exactly what submitting a whole
	// week writes. The declined week would then never relax, and the picker's floor would put it
	// out of reach entirely. The target entity's own valid range still applies here; only the
	// lock is deferred, to the effect below.
	const [selectedDate, setSelectedDate] = useState(() =>
		getInitialOrFirstNonLockValidDateForTargetEntity(initialDate, null, targetEntity)
	);
	// Two questions, one round trip: has the approver sent back the week the selected date is in,
	// and which of the weeks behind the submit lock have been sent back. The second one is needed
	// because the lock is also the picker's floor — asking only about the selected week, a week
	// could never be discovered as declined from any surface that opens on today, since while it
	// reads as not-declined every date on or before the lock is disabled in the picker.
	const {
		declined: selectedWeekDeclined,
		answered: weekAnswered,
		declinedWeekStarts,
	} = useIsPersonWeekDeclined(currentPersonId, selectedDate, submissionLockDate);

	const companyOnlyLockDate = getCompanyOnlyLockDateFromViewer(viewer);
	// What decides whether the *selected* date is accepted, and it stays keyed on that date's own
	// week: a decline says nothing about the other weeks the submit lock covers.
	const lockDate = selectedWeekDeclined ? companyOnlyLockDate : submissionLockDate;
	// What bounds the picker, which has to be the more permissive answer — a week cannot be
	// selected before it is reachable. Only a real company lock is a hard floor once anything
	// behind the submit lock has been sent back; the dates in that range that are still locked are
	// greyed out individually below rather than cut off wholesale.
	const someWeekDeclined = selectedWeekDeclined || declinedWeekStarts.length > 0;
	const pickerLockDate = someWeekDeclined ? companyOnlyLockDate : submissionLockDate;

	// Which of the dates the floor now exposes are still the submit lock's. Everything on or before
	// that lock is, except the days of a week known to have been sent back — so the picker greys
	// what it will not accept instead of offering it and failing on save. Days inside a declined
	// week that hold approved or invoiced time and no declined row are the one case this cannot
	// express: the veto for those is per row and lives on the server.
	const isDateBlockedBySubmissionLock = useCallback(
		(date: {year: number; month: number; day: number}) =>
			// Only while the floor is down. With the floor still at the submission lock these dates
			// are already out of range, and greying them would be describing a bound twice.
			someWeekDeclined && isDateHeldBySubmissionLock(date, submissionLockDate, declinedWeekStarts),
		[submissionLockDate, someWeekDeclined, declinedWeekStarts]
	);

	// Now do the advance the seed skipped, against the lock that actually applies. Waits for
	// `weekAnswered`, because in flight the week reads as not-declined and advancing on that would
	// land past a lock the real answer was about to lift. Runs at most once, and only while the
	// user has not picked a date themselves, so it can never fight their choice or oscillate as
	// the answer for each newly-landed-on week arrives.
	const openingDateSettled = useRef(false);
	useEffect(() => {
		if (openingDateSettled.current || !weekAnswered) return;
		openingDateSettled.current = true;
		if (!lockDate || differenceInCalendarDays(lockDate, selectedDate) < 0) return;
		const advanced = getInitialOrFirstNonLockValidDateForTargetEntity(selectedDate, lockDate, targetEntity);
		// The entity's range wins over the lock, as it did when this was a single call: seeding on
		// the entity's closest valid date deliberately ignores the lock, and applying the lock on
		// top of that answer would move the date back outside the task's or project's own dates —
		// opening the modal on a date that immediately fails entity validation instead of one that
		// only fails the lock, which the picker explains.
		if (getIsDateDisabledFromTargetEntity({date: advanced, targetEntity})) return;
		setSelectedDate(advanced);
	}, [weekAnswered, lockDate, selectedDate, targetEntity]);

	// Any date the user picks themselves also closes the one-shot advance above, so a slow week
	// answer cannot arrive afterwards and move the date out from under them.
	const selectDate: React.Dispatch<React.SetStateAction<Date>> = useCallback(value => {
		openingDateSettled.current = true;
		setSelectedDate(value);
	}, []);

	const [dateError, setDateError] = useState(false);
	const [formValidationFailed, setFormValidationFailed] = useState(false);
	const [noteError, setNoteError] = useState(false);
	const [showMovedToProjectRoleWarning, setShowMovedToProjectRoleWarning] = useState<boolean>(false);

	const [waiting, setWaiting] = useState(false);
	const {spinning} = useSpinner({waiting});

	const [selectedHarvestTask, setSelectedHarvestTask] = useState<FormattedHarvestTask | null>(initialHarvestTaskId);
	const [selectedUnit4Activity, setSelectedUnit4Activity] = useState<FormattedUnit4Activity | null>(null);

	const defaultRole = useMemo(() => {
		return initialState?.role !== undefined
			? initialState.role
			: getDefaultRole(targetEntity, viewer.company?.person?.role as Role);
	}, [targetEntity, viewer.company?.person?.role, initialState?.role]);

	const [selectedRole, setSelectedRole] = useState<Role | undefined>(defaultRole);

	const intl = useIntl();
	const allowedEntities = [SEARCH_ENTITIES.TASK, SEARCH_ENTITIES.PROJECT];

	if (isInternalTimeRegistrationAllowed()) {
		allowedEntities.push(SEARCH_ENTITIES.INTERNAL_TIME);
	}

	function handleEventTracking(value: string) {
		trackEvent('Shortcut', 'Pressed', {value});
	}

	useEffect(() => {
		if (defaultRole?.id) {
			setSelectedRole(defaultRole);
		}
	}, [defaultRole?.id]);

	useTrackModal('Time Registration Modal');

	const initialTargetEntityId = useRef(targetEntity?.id);
	const billableMinutesUpdated = useRef(false);

	const [isCommittingData, setIsCommittingData] = useState(false);
	const searchButtonRefPrimary = useRef<HTMLButtonElement>(null);
	const searchButtonRefSecondary = useRef<HTMLButtonElement>(null);
	const hoursInputRef = useRef<HTMLInputElement>(null);
	const notesInputRef = useRef<HTMLTextAreaElement>(null);

	const handleHarvestTaskChange = harvestTask => {
		setSelectedHarvestTask(harvestTask);
	};

	const handleUnit4ActivityChange = unit4Activity => {
		setSelectedUnit4Activity(unit4Activity);
	};
	const clearPrimarySelected = (selectedItem: FormattedTargetEntity) => {
		if (selectedItem) {
			// When clearing the primary selector, we do a refetch without any entity ids, since that will reset
			// all entities, clearing both the primary and secondary selector.
			setTargetEntityFetching(true);
			relay.refetch({taskId: null, projectId: null}, null, () => setTargetEntityFetching(false));
		}
	};

	const clearSecondarySelected = (selectedItem: FormattedTargetEntity) => {
		if (selectedItem?.project?.id) {
			// We are clearing the secondary selector(task), by doing a refretch with only the tasks project,
			// since the tasks project is the entity in the primary selector
			const backendEntityId = {taskId: null, projectId: idFromGlobalId(selectedItem.project.id)};
			setSecondaryTargetEntityFetching(true);
			relay.refetch(backendEntityId, null, () => setSecondaryTargetEntityFetching(false));
		}
	};

	const onSearchSelection = useCallback(searchResult => {
		const id = parseInt(searchResult.id);
		const projectId = parseInt(searchResult.searchResult.projectId);
		if (id && !isNaN(id)) {
			setTargetEntityFetching(true);
			const variant = searchResult.variant;
			const backendEntityId = searchResultToBackendEntityId({id, projectId, variant});
			relay.refetch(backendEntityId, null, () => setTargetEntityFetching(false));
		}
	}, []);

	const onSecondarySearchSelection = useCallback(searchResult => {
		const id = parseInt(searchResult.id);
		const projectId = parseInt(searchResult.searchResult.projectId);
		if (id && !isNaN(id)) {
			setSecondaryTargetEntityFetching(true);
			const variant = searchResult.variant;
			const backendEntityId = searchResultToBackendEntityId({id, projectId, variant});
			relay.refetch(backendEntityId, null, () => setSecondaryTargetEntityFetching(false));
		}
	}, []);

	const updateTime = useCallback(
		value => {
			setTime(Util.convertTimeInputToMinutes(value, true) || 0);
		},
		[setTime]
	);

	const onHoursInputFormat = useCallback(
		value => {
			updateTime(value != null ? `${value}h` : undefined);
		},
		[updateTime]
	);

	const onHoursInputChange = useCallback(
		value => {
			trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Time Input Changed', {from: time, to: value});
			updateTime(value);
			setTypedMinutes(null);
		},
		[updateTime, time]
	);

	useUpdateEffect(() => {
		if (!billableMinutesUpdated.current) {
			// Set billable time with time entry when time changes and billable time has not been manually updated or clciked in.
			setBillableTime(time);
		}
	}, [time]);

	const onBillableHoursInputChange = value => {
		const formattedValue = Util.convertTimeInputToMinutes(value, true);

		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Billable Time Input Changed', {
			from: billableTime,
			to: formattedValue,
		});
		setBillableTime(formattedValue);

		billableMinutesUpdated.current = true;
	};

	function onNoteChange(value: string): void {
		setNoteError(false);
		setNote(value);
	}

	function handleModalInputInvalid(modalInputInvalidReasons: ModalInvalidInputReason[]) {
		let elementFocused = false; // store if error handling has already focused an element to prevent trying to focus multiple elements and giving unpredictable behavior
		modalInputInvalidReasons.forEach(modalInputInvalidReason => {
			if (modalInputInvalidReason === ModalInvalidInputReason.InvalidTime) {
				hoursInputRef.current?.focus();
				elementFocused = true;
			}
			if (modalInputInvalidReason === ModalInvalidInputReason.InvalidNote) {
				setNoteError(true);
				if (!elementFocused) {
					notesInputRef.current?.focus();
					elementFocused = true;
				}
			}
		});
	}

	function validate(): boolean {
		if (!targetEntity) {
			return false;
		}
		if (dateError || timeError) {
			setFormValidationFailed(true);
			return false;
		} else {
			setFormValidationFailed(false);
		}

		const modalInputInvalidReasons = isModalInputInvalid({
			targetEntity,
			actualPersonId: viewer.actualPersonId,
			time,
			billableTime,
			note,
		});
		if (modalInputInvalidReasons.length > 0) {
			handleModalInputInvalid(modalInputInvalidReasons);
			return false;
		}

		return true;
	}

	function getBillableMinutesRegistered() {
		// If billable time has not been manually set but time has a value, use the time value as billable time.
		// Adding this as a fallback when the modal is pre-populated and the billable time has not
		// been manually set or clicked in.
		const billableMinutes = billableTime != null ? billableTime : time > 0 ? time : undefined;
		return getBillableMinutesForMutation(isTargetEntityBillable(targetEntity), billableMinutes);
	}

	function getRetainerPanelEntryMinutes(): number {
		return panelEntryMinutes({
			typedMinutes,
			billableEditedByHand: billableMinutesUpdated.current,
			entityBillable: isTargetEntityBillable(targetEntity),
			committedBillableMinutes: getBillableMinutesRegistered(),
		});
	}

	function trackSaveEvent(isCreateNew: boolean) {
		if (!targetEntity) {
			return;
		}
		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Submitted', {
			timeRegistrationPersonId: viewer?.company?.person?.id,
			timeRegistrationPersonEmail: viewer?.company?.person?.email,
			timeRegistrationTargetId: targetEntity?.id,
			timeRegistrationTargetType: TimeRegistrationEntity[targetEntity.entity],
			timeRegistrationTargetName: targetEntity?.name,
			minutesRegistered: time,
			billableMinutesRegistered: getBillableMinutesRegistered(),
			date: selectedDate.toDateString(),
			note,
			timeRegistrationRoleName: selectedRole?.name,
			usingSaveAndCreateNew: isCreateNew ? true : undefined,
		});
	}

	function commitData(onSuccess: () => void) {
		if (!targetEntity || !viewer.company?.id || !viewer.company.person?.id) return;
		if (isCommittingData) {
			return;
		}
		setIsCommittingData(true);
		setWaiting(true);

		callbackPositive({
			companyId: viewer.company.id,
			personId: viewer.company.person?.id,
			targetEntity,
			time,
			billableTime: getBillableMinutesRegistered(),
			date: selectedDate,
			note,
			harvestTaskId: selectedHarvestTask ? selectedHarvestTask.value : undefined,
			unit4ActivityId: selectedUnit4Activity ? selectedUnit4Activity.value : undefined,
			unit4ActivityName: selectedUnit4Activity ? selectedUnit4Activity.label : undefined,
			roleId: selectedRole?.id,
			onCreateSuccessCallbacks: [onSuccess],
			// A refused save keeps the form as entered, so the user can change the date and retry.
			onCreateFailureCallbacks: [
				() => {
					setWaiting(false);
					setIsCommittingData(false);
				},
			],
		});
	}

	function resetForm() {
		setTargetEntityFetching(true);
		relay.refetch({taskId: null, projectId: null}, null, () => setTargetEntityFetching(false));
		setNote('');
		setTime(0);
		setBillableTime(0);
	}

	const initializeForm = useCallback(initialState => {
		// At the time of this comment, we distpach the TIMESHEET_SHOW_SIDE_MODAL
		// event from two places, TimesheetsCalendar.tsx and CalendarEventModalLogic.tsx
		// and in both we distpach and pass date in the event but only in one we pass all the fields
		// below. That is why we need still need the below setters.
		// Changed as part of T58047

		const {note, minutes, date, callAttention, clearSearchEntity} = initialState;

		if (clearSearchEntity) {
			setTargetEntityFetching(true);
			relay.refetch({taskId: null, projectId: null}, null, () => setTargetEntityFetching(false));
		}

		if (note != null) setNote(note);
		if (minutes != null) setTime(minutes);
		if (date != null) {
			openingDateSettled.current = true;
			setSelectedDate(date);
		}
		if (callAttention != null) setCallAttention(callAttention);
	}, []);

	useSubscription(EVENT_ID.TIMESHEET_SHOW_SIDE_MODAL, initializeForm);

	function validateAndCallbackPositive() {
		if (validate()) {
			const onSuccess = () => {
				trackSaveEvent(false);
				setWaiting(false);
				if (showAsModal && closeModal) {
					closeModal();
				} else {
					resetForm();
				}
				setIsCommittingData(false);
			};
			commitData(onSuccess);
		}
	}

	function saveAndCreateNew(): void {
		if (validate()) {
			const onSuccess = () => {
				trackSaveEvent(true);
				setWaiting(false);
				if (closeModal) {
					closeModal();
				}
				showModal({
					type: MODAL_TYPE.CREATE_TIME_REGISTRATION,
					personId: viewer?.company?.person?.id,
					initialState: {
						date: selectedDate,
					},
				});
				setIsCommittingData(false);
			};
			commitData(onSuccess);
		}
	}

	function handleShortcutTimeReg(event: KeyboardEvent): void {
		const isMac = OsUtil.isMac();
		const ctrlKey = isMac ? event.metaKey : event.ctrlKey;
		const shiftKey = event.shiftKey;

		switch (event.key) {
			case 'Enter':
				if (ctrlKey) {
					if (shiftKey) {
						if (hasSecondaryButton) {
							handleEventTracking('time-reg-save-and-create-new');
							saveAndCreateNew();
						}
					} else {
						handleEventTracking('time-reg-save');
						validateAndCallbackPositive();
					}
				}
				break;
			default:
				break;
		}
	}

	useEffect(() => {
		window.addEventListener('keydown', handleShortcutTimeReg);
		return () => {
			window.removeEventListener('keydown', handleShortcutTimeReg);
		};
	}, [handleShortcutTimeReg]);

	function onCloseModal() {
		trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Closed');
		if (closeModal) {
			closeModal();
		}
	}

	useEffect(() => {
		if (targetEntity && time === 0) {
			hoursInputRef.current?.focus();
		} else {
			searchButtonRefPrimary.current?.focus();
		}
	}, []);

	// If the initial targetEntity has not been changed and the modal is in onlyValidateOnChange mode, subtract the initialTime.
	const subtractMinutes = useMemo(() => {
		if (onlyValidateFormElementsOnChange && initialTargetEntityId.current === targetEntity?.id) {
			return initialMinutes;
		}
	}, [initialMinutes, initialTargetEntityId.current, targetEntity, onlyValidateFormElementsOnChange]);

	const timeInputError = useMemo(() => {
		function getTaskRemainingMinutes(task: TimeRegistrationModal_task): number | null {
			if (!task || task.estimateForecastMinutes === null || task.totalMinutesRegistered === null) {
				return null;
			}
			return task.estimateForecastMinutes - task.totalMinutesRegistered;
		}

		return isTimeRegistrationTimeInvalidWithReason({
			inputMinutes: time,
			remainingMinutes: getTaskRemainingMinutes(viewer.task),
			subtractMinutes,
		});
	}, [time, viewer.task, subtractMinutes]);

	useEffect(() => {
		setTimeError(!!timeInputError);
	}, [timeInputError]);

	const windowSize = useWindowSize();
	const windowHeight = windowSize?.height ?? window.innerHeight;
	const contentMaxHeight = maxContentHeight ?? windowHeight - (fromTaskModal ? 600 : 300);

	const company = viewer.company;
	const person = company?.person;
	const personId = person ? idFromGlobalId(person.id) : null;

	const noteRequired = isTimeRegistrationNoteRequired();
	const billableSplitAllowed = isBillableSplitAllowed();
	const rolesAllowed = isRolesAllowed();
	const targetEntityAllowRoles = doesTargetEntityAllowRoles(targetEntity);
	const showRoles = rolesAllowed && targetEntityAllowRoles;

	const targetEntityBillable = isTargetEntityBillable(targetEntity);
	const entityProject = getEntityProject(targetEntity);

	const previousProjectId = usePrevious(entityProject?.id);

	useEffect(() => {
		const newDefaultRole = getDefaultRole(targetEntity, viewer.company?.person?.role as Role) || undefined;
		updateRoleOnProjectChange({
			newDefaultRole: newDefaultRole,
			initialRole: initialState?.role,
			projectId: entityProject?.id,
			previousProjectId: previousProjectId || undefined,
			selectedRole: selectedRole,
			setSelectedRole: setSelectedRole,
			setShowMovedToProjectRoleWarning: setShowMovedToProjectRoleWarning,
		});
	}, [initialState?.role?.id, entityProject?.id, viewer?.task?.role, viewer?.project?.projectPerson?.role]);

	const onRoleManuallySelected = useCallback(
		(role: Role) => {
			setSelectedRole(role);
			setShowMovedToProjectRoleWarning(false);
		},
		[setSelectedRole, setShowMovedToProjectRoleWarning]
	);

	const billableTimeInputValue = getBillableTimeInputValue_timeRegInputConversion(targetEntityBillable, billableTime, time);

	const workingHoursForDay = workingHourForTheDay(person, Moment(selectedDate) || Moment());

	const targetIsProjectAndProjectTimeNotAllowed =
		targetEntity && isProjectEntity(targetEntity.entity) && !isProjectTimeRegistrationAllowed();

	const modalDisabled =
		!targetEntity ||
		targetIsProjectAndProjectTimeNotAllowed ||
		(dateError && formValidationFailed) ||
		timeError ||
		isCommittingData ||
		waiting ||
		retainerMoveRefused;

	return (
		<TimeRegCreativeModal
			showAsModal={showAsModal}
			showHeader={!fromTaskModal}
			size={'xl'}
			closeModal={onCloseModal}
			creativeButtonText={infoText.creativeButtonText}
			secondaryButtonText={infoText.secondaryButtonText}
			onCreateHandler={validateAndCallbackPositive}
			onSecondaryHandler={hasSecondaryButton ? resetTimer ?? saveAndCreateNew : undefined}
			title={infoText.headerText}
			tooltip={<TimeRegistrationSettingsInfoMessage />}
			disabled={modalDisabled}
			waiting={spinning}
			replicateDesignSystem
			showCloseButton={showCloseButton}
		>
			<DeprecatedScrollToNativeScroll
				maxHeight={`${contentMaxHeight}px`}
				nativeMaxHeight={contentMaxHeight}
				hasFocusableContent
			>
				<FlexColumn gap={'l'}>
					{showRoles && showMovedToProjectRoleWarning && (
						<Alert
							text={intl.formatMessage({id: 'time_reg.changed_project_role_changed'})}
							type={AlertType.INFORMATION}
							onDismiss={() => setShowMovedToProjectRoleWarning(false)}
						/>
					)}
					{targetEntityPrimaryError ? <TimerTaskInvalidInfo invalidatedTimerTask={targetEntityPrimaryError} /> : null}
					{targetEntitySecondaryError ? (
						<TimerTaskInvalidInfo invalidatedTimerTask={targetEntitySecondaryError} />
					) : null}
					{!fromTaskModal && (
						<FlexColumn>
							<Label aria-label="Search for a task project or internal time">
								{infoText.searchFieldText
									? infoText.searchFieldText
									: `Find a ${getAllowedEntitiesText(allowedEntities, intl)}`}
							</Label>
							<TimeRegistrationModalSearch
								ref={searchButtonRefPrimary}
								targetEntity={targetEntityPrimary}
								targetEntityFetching={targetEntityFetching}
								companyId={company ? company.id : null}
								personId={personId}
								onResultSelected={onSearchSelection}
								clearSelected={clearPrimarySelected}
								allowedEntities={allowedEntities}
								dataCy={'time-registration-primary-modal-search-button'}
								trackingObject={TRACKING_OBJECTS.TIME_REGISTRATION_MODAL_SEARCH}
								disabled={!!jiraTaskId}
								makeWider={!showAsModal}
							/>
							{targetEntityPrimary && isTaskEntity(targetEntityPrimary.entity) ? (
								<TimeRegistrationModalTaskInfo
									task={asFragment<TimeRegistrationModalTaskInfo_task>(targetEntityPrimary)}
									initialTime={initialMinutes}
									addInitialTimeToRemaining={addInitialTimeToRemaining}
								/>
							) : null}
							{targetEntityPrimary && isProjectEntity(targetEntityPrimary.entity) ? (
								<TaskSelectionWrapper>
									<Label>
										{targetIsProjectAndProjectTimeNotAllowed ? 'Task (Required)' : 'Task (Optional)'}
									</Label>
									<TimeRegistrationModalSearch
										ref={searchButtonRefSecondary}
										targetEntity={targetEntitySecondary}
										targetEntityFetching={secondaryTargetEntityFetching}
										showTaskIndicator={true}
										companyId={company ? company.id : null}
										personId={personId}
										onResultSelected={onSecondarySearchSelection}
										clearSelected={clearSecondarySelected}
										projectId={idFromGlobalId(targetEntityPrimary.id)}
										baseSearch={'type:task'}
										allowedEntities={[SEARCH_ENTITIES.TASK]}
										dataCy={'time-registration-secondary-modal-search-button'}
										trackingObject={TRACKING_OBJECTS.TIME_REGISTRATION_MODAL_SEARCH_SECONDARY}
										disabled={!!jiraTaskId}
										makeWider={!showAsModal}
									/>
								</TaskSelectionWrapper>
							) : null}
							{targetEntitySecondary && isTaskEntity(targetEntitySecondary.entity) ? (
								<TimeRegistrationModalTaskInfo
									task={asFragment<TimeRegistrationModalTaskInfo_task>(targetEntitySecondary)}
									initialTime={initialMinutes}
									addInitialTimeToRemaining={addInitialTimeToRemaining}
								/>
							) : null}
						</FlexColumn>
					)}
					<CallAttentionWrapper callAttention={callAttention} onAnimationEnd={() => setCallAttention(false)}>
						<TimeRegistrationModalDates
							personId={person ? person.id : null}
							targetEntity={targetEntity}
							lockDate={lockDate}
							pickerLockDate={pickerLockDate}
							isDateBlockedByLock={isDateBlockedBySubmissionLock}
							selectedDate={selectedDate}
							setSelectedDate={selectDate}
							setDateError={setDateError}
							formValidationFailed={formValidationFailed}
							width={'200px'}
						/>
					</CallAttentionWrapper>
					<TimeRegistrationModalIntegrations
						project={asFragment<TimeRegistrationModalIntegrations_project>(entityProject)}
						person={asFragment<TimeRegistrationModalIntegrations_person>(person)}
						company={asFragment<TimeRegistrationModalIntegrations_company>(company)}
						selectedHarvestTask={selectedHarvestTask}
						handleHarvestTaskChange={handleHarvestTaskChange}
						handleUnit4ActivityChange={handleUnit4ActivityChange}
						selectedUnit4Activity={selectedUnit4Activity}
					/>
					<FlexColumn>
						<FlexRow gap={'l'}>
							<FlexColumn flex="0" gap={'xs'}>
								<FlexRow>
									<Label>{intl.formatMessage({id: 'common.time_entry'})}</Label>
									<TimeRegGranularityInfoIcon />
								</FlexRow>
								<TimeRegWSuggestionsInput
									id={'time-add-hours-input'}
									initialValue={`${time}m`} // Converted to hours since hours input works in hours
									onInputChange={onHoursInputChange}
									onTyping={setTypedMinutes}
									onHoursInputFormatted={
										// FIXME: Investigate if we can get rid of this hacky solution without breaking shit.
										onHoursInputFormat
									}
									date={Moment(selectedDate)}
									task={viewer.task}
									idleTime={viewer.idleTime}
									personId={person ? person.id : null}
									person={person}
									innerRef={hoursInputRef}
									userpilot={'time-registration-modal-hours-input'}
									onlyFormatGranularityOnBlur={onlyValidateFormElementsOnChange}
									width={'120px'}
									replicateDesignSystem
									clearable
								/>
							</FlexColumn>
							{billableSplitAllowed && (
								<FlexColumn flex="0" gap={'xs'}>
									<FlexRow>
										<Label>{intl.formatMessage({id: 'common.billable_time'})}</Label>
										<TimeRegGranularityInfoIcon />
									</FlexRow>
									<HoursInput
										customClassName={'hour-input'}
										cy={'new-billable-time-entry-input'}
										value={billableTimeInputValue}
										mutation={onBillableHoursInputChange}
										disabled={!targetEntityBillable}
										replicateDesignSystem={true}
										width={'120px'}
										onlyFormatGranularityOnBlur={onlyValidateFormElementsOnChange}
										granularityFormatter={(val, intl, minuteLimit, showAsterisk) =>
											convertIntoFloatHoursFormatWithGranularity(
												val,
												intl,
												minuteLimit,
												showAsterisk,
												workingHoursForDay
											)
										}
										isTimeregInput
									/>
								</FlexColumn>
							)}
							{showRoles && showAsModal && (
								<FlexColumn flex="1">
									<SkylineRoleDropdownRelayWrapper
										role={selectedRole}
										onSelect={onRoleManuallySelected}
										projectId={entityProject?.id}
										controlled
									/>
								</FlexColumn>
							)}
						</FlexRow>
						{showRoles && !showAsModal && (
							<div style={{marginTop: '12px'}}>
								<SkylineRoleDropdownRelayWrapper
									role={selectedRole}
									onSelect={onRoleManuallySelected}
									projectId={entityProject?.id}
									controlled
								/>
							</div>
						)}
						{timeInputError ? <Text color={'error'}>{timeInputError}</Text> : null}
					</FlexColumn>
					<TimeRegistrationModalRetainerPeriod
						projectId={entityProject?.id ?? null}
						taskId={targetEntity && isTaskEntity(targetEntity.entity) ? targetEntity.id : null}
						date={selectedDate}
						entryBillableMinutes={getRetainerPanelEntryMinutes()}
						timeRegistrationId={timeRegistrationId}
						onMoveRefusedChange={setRetainerMoveRefused}
					/>
					<CallAttentionWrapper callAttention={callAttention} onAnimationEnd={() => setCallAttention(false)}>
						<TextAreaWrapper>
							<TextArea
								ref={notesInputRef}
								label="Note"
								height={80}
								value={note}
								isOptional={!noteRequired}
								placeholder="Enter a brief description of your activity"
								data-cy={'time-registration-modal-notes'}
								onChange={onNoteChange}
								onBlur={() => trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Note Input Updated')}
								errorText={
									noteError ? intl.formatMessage({id: 'time_registration.note_required_error'}) : undefined
								}
								maxLength={999}
							/>
						</TextAreaWrapper>
					</CallAttentionWrapper>
				</FlexColumn>
			</DeprecatedScrollToNativeScroll>
		</TimeRegCreativeModal>
	);
}

export const TimeRegistrationModalQuery = graphql`
	query TimeRegistrationModal_Query(
		$personId: ID
		$taskId: Int
		$projectId: Int
		$idleTimeId: Int
		$fetchProgramInfo: Boolean!
	) {
		viewer {
			actualPersonId
			component(name: "time_registration_modal")
			...TimeRegistrationModal_viewer
				@arguments(
					personId: $personId
					taskId: $taskId
					projectId: $projectId
					idleTimeId: $idleTimeId
					fetchProgramInfo: $fetchProgramInfo
				)
		}
	}
`;

export default createRefetchContainer(
	TimeRegistrationModal,
	{
		viewer: graphql`
			fragment TimeRegistrationModal_viewer on Viewer
			@argumentDefinitions(
				personId: {type: "ID"}
				taskId: {type: "Int"}
				projectId: {type: "Int"}
				idleTimeId: {type: "Int"}
				fetchProgramInfo: {type: "Boolean!"}
			) {
				backendId
				actualPersonId
				company {
					id
					sageIntacctEnabled
					lockedPeriodYear
					lockedPeriodMonth
					lockedPeriodDay
					roles(includeDisabled: false, backendProjectId: $projectId) {
						edges {
							node {
								id
								name
							}
						}
					}
					...TimeRegistrationModalIntegrations_company
					person(id: $personId) {
						id
						role {
							id
							name
						}
						email
						submitLockedDateYear
						submitLockedDateMonth
						submitLockedDateDay
						excludeFromCompanyLockedPeriod
						startDate # used in HoursInput, should eventually be refactored to fragment
						endDate # used in HoursInput, should eventually be refactored to fragment
						createdAt # used in HoursInput, should eventually be refactored to fragment
						monday # used in HoursInput, should eventually be refactored to fragment
						tuesday # used in HoursInput, should eventually be refactored to fragment
						wednesday # used in HoursInput, should eventually be refactored to fragment
						thursday # used in HoursInput, should eventually be refactored to fragment
						friday # used in HoursInput, should eventually be refactored to fragment
						saturday # used in HoursInput, should eventually be refactored to fragment
						sunday # used in HoursInput, should eventually be refactored to fragment
						...TimeRegistrationModalIntegrations_person
					}
				}
				task(backendId: $taskId) {
					id
					name
					role {
						id
						name
					}
					companyTaskId
					estimateForecastMinutes
					totalMinutesRegistered
					timeLeft
					billable
					startYear
					startMonth
					startDay
					deadlineYear
					deadlineMonth
					deadlineDay
					statusColumnV2 {
						category
					}
					assignedPersons {
						id
					}
					project {
						id
						name
						stage {
							id
							name
							profile
						}
						fullAccessToProject
						companyProjectId
						customProjectId
						projectColor
						billable
						projectStartYear
						projectStartMonth
						projectStartDay
						projectEndYear
						projectEndMonth
						projectEndDay
						projectRoles {
							id
							name
						}
						projectPerson(personId: $personId) {
							role {
								id
								name
							}
						}
						budgetType @include(if: $fetchProgramInfo)
						defaultPeriodBudgetType @include(if: $fetchProgramInfo)
						programBudgetType @include(if: $fetchProgramInfo)
						isProgramRevenueLocked @include(if: $fetchProgramInfo)
						program @include(if: $fetchProgramInfo) {
							name
							prefix
							members {
								edges {
									node {
										role
										person {
											id
										}
									}
								}
							}
						}
						...ProjectIndicator_project
						...TimeRegistrationModalIntegrations_project
					}
					...TimeRegistrationModalTaskInfo_task
				}
				project(backendId: $projectId) {
					id
					name
					stage {
						id
						name
						profile
					}
					fullAccessToProject
					companyProjectId
					customProjectId
					projectColor
					billable
					projectStartYear
					projectStartMonth
					projectStartDay
					projectEndYear
					projectEndMonth
					projectEndDay
					client {
						name
					}
					projectRoles {
						id
						name
					}
					projectPerson(personId: $personId) {
						role {
							id
							name
						}
					}
					budgetType @include(if: $fetchProgramInfo)
					defaultPeriodBudgetType @include(if: $fetchProgramInfo)
					programBudgetType @include(if: $fetchProgramInfo)
					isProgramRevenueLocked @include(if: $fetchProgramInfo)
					program @include(if: $fetchProgramInfo) {
						name
						prefix
						members {
							edges {
								node {
									role
									person {
										id
									}
								}
							}
						}
					}
					...ProjectIndicator_project
					...TimeRegistrationModalIntegrations_project
				}
				idleTime(backendId: $idleTimeId) {
					id
					isInternalTime
					name
				}
			}
		`,
	},
	graphql`
		query TimeRegistrationModalRefetchQuery(
			$personId: ID
			$taskId: Int
			$projectId: Int
			$idleTimeId: Int
			$fetchProgramInfo: Boolean!
		) {
			viewer {
				component(name: "time_registration_modal_refetch")
				...TimeRegistrationModal_viewer
					@arguments(
						personId: $personId
						taskId: $taskId
						projectId: $projectId
						idleTimeId: $idleTimeId
						fetchProgramInfo: $fetchProgramInfo
					)
			}
		}
	`
);
